// Factory function pattern to avoid Webpack TDZ issues with class inheritance
// This uses BaseSuperAdminFactory instead of class inheritance

import { createBaseSuperAdmin, type IBaseSuperAdmin } from "../BaseSuperAdminFactory";
import type { Deed } from "le-coffre-resources/dist/Notary";
import type { QueryPayload } from "../../../types";
import type { IGetDeedsParams, IPutDeedsParams } from "./Deeds";
import { buildDeedsUpdatePayload } from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";

interface DeedsInstance {
	get(q?: IGetDeedsParams): Promise<Deed[]>;
	getByUid(uid: string, q?: QueryPayload): Promise<Deed>;
	put(uid: string, body: IPutDeedsParams): Promise<Deed>;
}

// Module-level singleton instance
let instance: DeedsInstance | null = null;
let baseSuperAdminInstance: IBaseSuperAdmin | null = null;

async function deedsGet(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string, q?: IGetDeedsParams): Promise<Deed[]> {
	const url = new URL(baseUrl);
	if (q) baseSuperAdmin.appendJsonSearchParams(url, q);
	try {
		return await baseSuperAdmin.getRequest<Deed[]>(url);
	} catch (err) {
		baseSuperAdmin.onError(err);
		return Promise.reject(err);
	}
}

async function deedsGetByUid(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string, uid: string, q?: QueryPayload): Promise<Deed> {
	const url = new URL(baseUrl.concat(`/${uid}`));
	if (q) baseSuperAdmin.appendJsonSearchParams(url, q);
	try {
		return await baseSuperAdmin.getRequest<Deed>(url);
	} catch (err) {
		baseSuperAdmin.onError(err);
		return Promise.reject(err);
	}
}

async function deedsPut(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string, uid: string, body: IPutDeedsParams): Promise<Deed> {
	const url = new URL(baseUrl.concat(`/${uid}`));
	try {
		return await baseSuperAdmin.putRequest<Deed>(url, buildDeedsUpdatePayload(body));
	} catch (err) {
		baseSuperAdmin.onError(err);
		return Promise.reject(err);
	}
}

function createDeeds(): DeedsInstance {
	if (!baseSuperAdminInstance) {
		baseSuperAdminInstance = createBaseSuperAdmin();
	}
	const baseSuperAdmin = baseSuperAdminInstance;
	const baseUrl = baseSuperAdmin.namespaceUrl.concat("/deeds");
	return {
		get: (q?: IGetDeedsParams) => deedsGet(baseSuperAdmin, baseUrl, q),
		getByUid: (uid: string, q?: QueryPayload) => deedsGetByUid(baseSuperAdmin, baseUrl, uid, q),
		put: (uid: string, body: IPutDeedsParams) => deedsPut(baseSuperAdmin, baseUrl, uid, body),
	};
}

export function getDeedsInstance(): DeedsInstance {
	if (!instance) {
		instance = createDeeds();
	}
	return instance;
}
